import { createFileRoute, Link } from "@tanstack/react-router";
import { PageHero } from "@/components/page-hero";
import {
  ArrowRight,
  Utensils,
  HardHat,
  Stethoscope,
  Store,
  Factory,
  Briefcase,
  HeartHandshake,
  Home,
} from "lucide-react";
import { industries } from "@/content/industries";
import { CTASection } from "@/components/cta-section";
import { InternalLinksHub } from "@/components/internal-links-hub";

export const Route = createFileRoute("/hr-by-industry/")({
  head: () => ({
    meta: [
      { title: "HR & Employment Law by Industry for UK Employers" },
      {
        name: "description",
        content:
          "Sector-specific HR and employment law guidance for UK employers in hospitality, construction, healthcare, care homes, charities, retail and manufacturing.",

      },
      { property: "og:title", content: "HR & Employment Law by Industry for UK Employers" },
      {
        property: "og:description",
        content: "Industry-tailored HR and employment law support for UK SMEs.",
      },
      { property: "og:url", content: "https://www.hrservicecentre.co.uk/hr-by-industry" },
    ],
    links: [{ rel: "canonical", href: "https://www.hrservicecentre.co.uk/hr-by-industry" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "HR & Employment Law by Industry",
          url: "https://www.hrservicecentre.co.uk/hr-by-industry",
          description:
            "Sector-specific HR and employment law guidance for UK businesses across hospitality, construction, healthcare, care homes, charities, professional services, retail and manufacturing.",
          isPartOf: { "@id": "https://www.hrservicecentre.co.uk/#website" },
          mainEntity: {
            "@type": "ItemList",
            itemListElement: industries.map((s, i) => ({
              "@type": "ListItem",
              position: i + 1,
              url: `https://www.hrservicecentre.co.uk/hr-by-industry/${s.slug}`,
              name: s.name,
            })),
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://www.hrservicecentre.co.uk/" },
            { "@type": "ListItem", position: 2, name: "Industries", item: "https://www.hrservicecentre.co.uk/hr-by-industry" },
          ],
        }),
      },
    ],
  }),
  component: IndustriesIndex,
});

const icons = {
  utensils: Utensils,
  hardhat: HardHat,
  stethoscope: Stethoscope,
  store: Store,
  factory: Factory,
  briefcase: Briefcase,
  truck: Store,
  school: Briefcase,
  heart: HeartHandshake,
  home: Home,
} as const;

function IndustriesIndex() {
  return (
    <>
      <PageHero
        breadcrumbSchema={false}
        breadcrumbs={[
          { label: "Home", path: "/" },
          { label: "Industries", path: "/hr-by-industry" },
        ]}
        eyebrow="Industries"
        title="Industry-Specific HR Support for UK Employers"
        subtitle="Specialist HR support tailored to your industry and workforce."
        intro="We work across the industries with the toughest people challenges, bringing sector-specific playbooks, case law and risk awareness to every engagement."
      />

      <section className="px-6 py-20 lg:px-8">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {industries.map((ind) => {
            const Icon = icons[ind.iconKey];
            return (
              <Link
                key={ind.slug}
                to="/hr-by-industry/$slug"
                params={{ slug: ind.slug }}
                className="group flex flex-col rounded-2xl bg-card p-8 ring-1 ring-black/5 transition hover:-translate-y-0.5 hover:ring-accent/30"
              >
                <span className="mb-6 inline-flex size-10 items-center justify-center rounded-lg bg-accent/10 text-accent">
                  <Icon className="size-5" />
                </span>
                <h2 className="font-serif text-xl text-brand">{ind.name}</h2>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-muted-foreground">{ind.summary}</p>
                <span className="mt-6 inline-flex items-center gap-1 text-sm font-medium text-accent">
                  Explore <ArrowRight className="size-4 transition-transform group-hover:translate-x-0.5" />
                </span>
              </Link>
            );
          })}
        </div>
      </section>

      <InternalLinksHub heading="Sector HR paired with the right services and resources" />
      <CTASection />
    </>
  );
}
